import { useUserMe } from "~/components/user/composables/useUser";
import { useSupabase } from "~/composables/useSupabase";
import type { WorkspaceType } from "@asap/shared/supabase/shared/types";
import { teamMemberColumns, workspaceColumns } from "@asap/shared/utils/supabase.columns";
import type { TeamMember, Workspace } from "@asap/shared/utils/supabase.types";
import { createSharedComposable } from "@vueuse/core";
import { chunk } from "lodash-es";
import { useManagees } from "~/components/team-member/composables/useManagees";

export const useAsapWorkspace = createSharedComposable(() => {
  const asapWorkspaceId = ref<string>();

  const supabase = useSupabase();

  watchEffect(async () => {
    const { data, error } = await supabase.from("workspace").select("id").eq("is_asap_workspace", true).maybeSingle();
    if (error) throw new Error(error.message);

    asapWorkspaceId.value = data?.id;
  });

  return { asapWorkspaceId };
});

export const useWorkspaceMe = createSharedComposable(function () {
  const teamMembers = ref<TeamMember[]>([]);
  const currentWorkspace = ref<Workspace>();

  const supabase = useSupabase();
  const {
    workspaceId,
    isSupportRole,
    isDeveloper,
    invitedWorkspaces,
    isIndependent,
    isIndependentAdministrator,
    isManager,
  } = useUserMe();
  const { areSomeManageesIndependent } = useManagees();
  const { asapWorkspaceId } = useAsapWorkspace();

  const isAsapWorkspace = computed<boolean>(() => workspaceId.value === asapWorkspaceId.value);
  const isIndependentWorkspace = computed<boolean>(() => !isAsapWorkspace.value);

  const canSwitchWorkspace = computed<boolean>(() => {
    if (isSupportRole.value || isDeveloper.value) return true;
    if (isIndependentAdministrator.value) return true;
    if (isAsapWorkspace.value && areSomeManageesIndependent.value) return true;
    if (isIndependent.value && invitedWorkspaces.value && invitedWorkspaces.value.length > 0) return true;
    if (isManager.value) return true;
    return false;
  });

  const getWorkspace = async () => {
    if (!workspaceId.value) return;

    const { data, error } = await supabase
      .from("workspace")
      .select(workspaceColumns)
      .eq("id", workspaceId.value)
      .single();

    if (error) throw error;

    currentWorkspace.value = data;
  };

  const getTeamMembers = async () => {
    if (!workspaceId.value) return;

    const { data: memberships, error: errorMemberships } = await supabase
      .from("team_member_workspace")
      .select()
      .eq("workspace_id", workspaceId.value);
    if (errorMemberships) throw errorMemberships;

    // Batch the team members ids by 50
    const chunks = chunk(
      memberships.map((membership) => membership.team_member_id),
      50
    );

    const promises = chunks.map(async (chunk) => {
      const { data, error } = await supabase
        .from("team_member")
        .select<string, TeamMember>(teamMemberColumns)
        .in("id", chunk);
      if (error) throw error;
      return data;
    });
    const fetchedTeamMembers = await Promise.allSettled(promises);
    teamMembers.value = fetchedTeamMembers.flatMap((result) => {
      if (result.status === "fulfilled") {
        return result.value;
      }
      return [];
    });
  };

  watchEffect(async () => {
    await getWorkspace();
    await getTeamMembers();
  });

  return {
    currentWorkspace,
    teamMembers,
    isAsapWorkspace,
    isIndependentWorkspace,
    canSwitchWorkspace,
    refetchTeamMembers: getTeamMembers,
  };
});

export const useWorkspaceFilter = createSharedComposable(() => {
  const workspaceType = ref<WorkspaceType>("internal");

  return {
    workspaceType,
  };
});
